import React, { useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  CustomTextInput,
  CustomSingleSelect,
  CustomSectionTitle,
  CustomCategorySelectSingle,
} from "@ippon/shared-components";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AREA_TYPE_OPTIONS } from "@ippon/shared-types";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBudgetCategories,
  createBudgetCategory,
} from "@/store/budgetSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import type { UnitFormValues } from "../UnitTabForm";
import UnitSectionCustomFields from "./UnitSectionCustomFields";
import EmployeeSelect from "./EmployeeSelect";

interface Props {
  form: UseFormReturn<UnitFormValues>;
  propertyId: string;
  /** 親シェルがすでに「区画情報」タイトルを出している場合は true で抑止 */
  hideTitle?: boolean;
  /** 区画詳細ページ: 場所区分を変更不可にする */
  disableAreaTypeSelect?: boolean;
  labelWidth?: string;
  inputWidth?: string;
}

const REVENUE_TYPE_OPTIONS = [
  { label: "収益なし", value: "none" },
  { label: "貸室", value: "stock_rent" },
  { label: "付帯", value: "stock_other" },
];

const BasicSection: React.FC<Props> = ({
  form,
  propertyId: _propertyId,
  hideTitle,
  disableAreaTypeSelect,
  labelWidth,
  inputWidth,
}) => {
  void _propertyId;
  const { register, control, setValue, watch, formState } = form;
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const revenueType = watch("revenue_type");

  // 収益カテゴリの変更はローカル保持のみ。stock → none の確認モーダルと
  // 関連データ削除は「更新」ボタン押下時 (UnitTabForm) でまとめて行う。
  const handleRevenueTypeChange = (next: unknown) => {
    const v = String(next || "");
    setValue("revenue_type", v as "stock_rent" | "stock_other" | "none", {
      shouldDirty: true,
    });
    // 付帯以外に切り替えたら収益項目をクリア
    if (v !== "stock_other") {
      setValue("revenue_item_category_id", undefined, { shouldDirty: true });
    }
  };

  // 付帯 (stock_other) の「収益項目」プルダウン用に収入カテゴリを取得。
  // 選択肢は revenue_group=stock_other の収入科目 (駐車場/看板/自販機/アンテナ/水光熱費 + ユーザー追加)。
  const budgetCategories = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.data || [],
  );
  useEffect(() => {
    if (revenueType === "stock_other") {
      dispatch(fetchBudgetCategories() as any);
    }
  }, [revenueType, dispatch]);

  const revenueItemOptions = useMemo(
    () =>
      (budgetCategories as any[])
        .filter(
          (c) =>
            c &&
            c.category_type === "income" &&
            c.revenue_group === "stock_other" &&
            !c.deleted_at,
        )
        .map((c) => ({ value: c.category_id, label: c.name })),
    [budgetCategories],
  );

  // 収益項目の新規追加 → BudgetCategory(stock_other) としてマスタに永続化し、
  // 返った category_id をフォームにセット (収入タブ・予実と共通の単一マスタ)。
  // 注: CustomCategorySelectSingle は追加直後に入力名 (= ラベル文字列) を一旦
  // onValueChange で選択値に入れるため、成功時は category_id で上書きし、
  // 失敗時はその不正値 (ラベル文字列) を必ずクリアする。
  const handleAddRevenueItem = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const res = await dispatch(
      createBudgetCategory({
        name: trimmed,
        category_type: "income",
        revenue_group: "stock_other",
      }) as any,
    );
    if (createBudgetCategory.fulfilled.match(res)) {
      setValue("revenue_item_category_id", (res.payload as any).category_id, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setValue("revenue_item_category_id", undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      showToast({ type: "error", message: "収益項目の追加に失敗しました" });
    }
  };

  const widthProps = { labelWidth, inputWidth } as const;

  return (
    <Box>
      {!hideTitle && (
        <Box sx={{ mb: 2 }}>
          <CustomSectionTitle title="共通情報" size="small" />
        </Box>
      )}
      <Stack spacing={0}>
        {/* 場所区分 → ↳ 名称 (修繕工事と同じ親子構造) */}
        <CustomSingleSelect
          label="場所区分"
          name="area_type"
          control={control}
          errors={formState.errors}
          options={[...AREA_TYPE_OPTIONS]}
          isRequired
          disabled={disableAreaTypeSelect}
          {...widthProps}
        />
        <Box sx={{ ml: 3, position: "relative" }}>
          <SubdirectoryArrowRightIcon
            sx={{
              position: "absolute",
              left: -20,
              top: 0,
              fontSize: 16,
              color: "#989898",
              pointerEvents: "none",
            }}
          />
          <CustomTextInput
            label="名称"
            name="landmark_name"
            register={register}
            errors={formState.errors}
            isRequired
            labelWidth={labelWidth}
            inputWidth={
              inputWidth ? `calc(${inputWidth} - 24px)` : undefined
            }
          />
        </Box>
        <CustomSingleSelect
          label="収益カテゴリ"
          name="revenue_type"
          control={control}
          errors={formState.errors}
          options={REVENUE_TYPE_OPTIONS}
          isRequired
          onChangeValue={handleRevenueTypeChange}
          {...widthProps}
        />
        {/* 付帯 (stock_other) のときだけ「収益項目」を ↳ 子行で表示。
            駐車場/看板/自販機/アンテナ/水光熱費 + ユーザー追加 (収入カテゴリと共通マスタ)。 */}
        {revenueType === "stock_other" && (
          <Box sx={{ ml: 3, position: "relative" }}>
            <SubdirectoryArrowRightIcon
              sx={{
                position: "absolute",
                left: -20,
                top: 0,
                fontSize: 16,
                color: "#989898",
                pointerEvents: "none",
              }}
            />
            <Controller
              name="revenue_item_category_id"
              control={control}
              rules={{
                validate: (v, formValues) =>
                  (formValues as UnitFormValues).revenue_type !==
                    "stock_other" ||
                  !!(v && String(v).trim()) ||
                  "収益項目を選択してください",
              }}
              render={({ field }) => (
                <CustomCategorySelectSingle
                  label="収益項目"
                  name="revenue_item_category_id"
                  isRequired
                  options={revenueItemOptions}
                  selectedValue={field.value || ""}
                  onValueChange={(val) => field.onChange(val)}
                  onAddNewOption={handleAddRevenueItem}
                  errors={formState.errors}
                  placeholder="収益項目を選択 / 追加"
                  labelWidth={labelWidth}
                  inputWidth={
                    inputWidth ? `calc(${inputWidth} - 24px)` : undefined
                  }
                />
              )}
            />
          </Box>
        )}
        <EmployeeSelect
          name="manager_employee_id"
          control={control}
          errors={formState.errors}
          {...widthProps}
        />
        <CustomTextInput
          label="備考"
          name="notes"
          register={register}
          errors={formState.errors}
          multiline
          rows={3}
          {...widthProps}
        />
        {/* セクション固有のカスタム項目 (= 共通情報のカスタムフォーム) */}
        <UnitSectionCustomFields
          form={form}
          sectionKey="basic"
          {...widthProps}
        />
      </Stack>
    </Box>
  );
};

export default BasicSection;
